import React from "react";
import {
  useCreateProjectPortfolio,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectPortfolios,
} from "@planzone/view-models";
import { useTranslation } from "react-i18next";
import { Identifiable, Project, ProjectPortfolio as PP } from "@planzone/dto";

import { Props } from "./types";
import { Form } from "./form";
import { ProjectPortfolio } from "./projectPortfolio";
import styles from "./styles.scss";

function extractAssignedPortfolios(
  project: Identifiable<Project>,
  portfolios: Identifiable<PP>[],
): Identifiable<PP>[] {
  return portfolios.reduce<Identifiable<PP>[]>(
    (sum, portfolio) =>
      !!portfolio.projects.find(({ uuid }) => uuid === project.uuid)
        ? [...sum, portfolio]
        : sum,
    [],
  );
}

function sanitize(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function PortfolioAssignation({ project }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { portfolios } = useRetrieveProjectPortfolios();
  const { isRequestPending, createProjectPortfolio } =
    useCreateProjectPortfolio();
  const [search, setSearch] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const assignedPortfolios = extractAssignedPortfolios(project, portfolios);
  const filteredPortfolios = !!search
    ? portfolios.filter(({ name }) =>
        new RegExp(sanitize(search), "i").test(name),
      )
    : portfolios;
  const unAssignedPortFolios = filteredPortfolios.filter(
    (portfolio) =>
      !assignedPortfolios.find(({ uuid }) => uuid === portfolio.uuid),
  );

  return (
    <div className={styles.portfolioAssignation}>
      <div className={styles.search}>
        <input
          value={search}
          className={styles.input}
          placeholder={t("portfolioAssignation.search")}
          onChange={({ target }) => setSearch(target.value)}
        />
      </div>
      <ul className={styles.list}>
        {unAssignedPortFolios.map((portfolio) => (
          <li key={portfolio.uuid} className={styles.item}>
            <ProjectPortfolio portfolio={portfolio} project={project} />
          </li>
        ))}
      </ul>
      <div className={styles.footer}>
        {!isOpen ? (
          <button
            className={styles.addPortfolio}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
          >
            {t("portfolioAssignation.add")}
          </button>
        ) : (
          <Form
            isRequestPending={isRequestPending}
            cancel={() => setIsOpen(false)}
            submit={(portfolio) => {
              createProjectPortfolio({ portfolio, planzone });
              setIsOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

export { PortfolioAssignation };
